import { Fragment, memo } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { type StreamMessage } from "../../hooks/useStreamingText";
import { Text } from "../Text";

import { ActivityLineRow, resolveActionLine } from "./ActivityLineRow";
import type { GroupedEntry } from "./groupChatEntries";
import { KickoffRow, resolveKickoff } from "./KickoffRow";
import { ResearchCitations } from "./ResearchCitations";
import { MessageRow } from "./MessageRow";
import { ToolGroupRow } from "./ToolGroupRow";
import {
  DateBadge,
  DateMarker,
  PrivateLabel,
  PrivateSection,
} from "./transcriptStyles";
import { UserActionGroupRow } from "./UserActionGroupRow";
import { PushLiveRequestedNote } from "./PushLiveRequestedNote";
import { WorkflowChangeReview } from "./WorkflowChangeReview";
import type { ChatProps } from "./chatTypes";
import {
  useTranscriptExpansion,
  useTranscriptSettings,
} from "./TranscriptContext";

function formatDateLabel(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const entryDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (entryDay.getTime() === today.getTime()) return "Today";
  if (entryDay.getTime() === yesterday.getTime()) return "Yesterday";

  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  };
  return date.toLocaleDateString("en-US", options);
}

function getGroupTimestamp(grouped: GroupedEntry): number {
  if (grouped.type === "single") return grouped.entry.createdAt;
  if (grouped.type === "userActionGroup")
    return grouped.entries[0]?.createdAt ?? 0;
  if (grouped.entries[0]) return grouped.entries[0].createdAt;
  return 0;
}

function isActivityRow(grouped: GroupedEntry): boolean {
  if (grouped.type !== "single") return true;
  const { entry } = grouped;
  return (
    entry.entryType === "SystemMessage" ||
    entry.entryType === "Compaction" ||
    entry.toolCall?.toolName === "seedBranchStorage" ||
    resolveKickoff(entry) !== null ||
    resolveActionLine(entry) !== null
  );
}

function getDateKey(timestamp: number): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

type TranscriptProps = {
  reviewVersion?: ChatProps["reviewVersion"];
  entries: readonly ChatEntryWithSubTables[];
  groupedEntries: GroupedEntry[];
  streamingEntries: ReadonlyMap<string, StreamMessage>;
};

export const Transcript = memo(function Transcript({
  reviewVersion,
  entries,
  groupedEntries,
  streamingEntries,
}: TranscriptProps) {
  const { setExpandedToolGroups } = useTranscriptExpansion();
  const { privateChatIds = [] } = useTranscriptSettings();
  const privateRows = groupedEntries.map(
    (grouped) =>
      privateChatIds.length > 0 &&
      (grouped.type === "single" ? [grouped.entry] : grouped.entries).every(
        (entry) => privateChatIds.includes(entry.chatId)
      )
  );
  const privateDateMarkers = new Map<number, React.ReactNode>();
  const showDiscussion = (entryId: string) => {
    const group = groupedEntries.find(
      (grouped) =>
        grouped.type === "toolGroup" &&
        grouped.entries.some((entry) => entry.id === entryId)
    );
    if (group?.type === "toolGroup") {
      setExpandedToolGroups((previous) => new Set(previous).add(group.groupId));
    }
  };
  const rowTimestamps: number[] = [];
  for (const grouped of groupedEntries) {
    rowTimestamps.push(
      Math.max(getGroupTimestamp(grouped), rowTimestamps.at(-1) ?? 0)
    );
  }
  const rows = groupedEntries.map((grouped, index) => {
    const startsPrivate = privateRows[index] && !privateRows[index - 1];
    const endsPrivate = privateRows[index] && !privateRows[index + 1];
    const nextGrouped = endsPrivate ? undefined : groupedEntries[index + 1];
    const prevGrouped = startsPrivate ? undefined : groupedEntries[index - 1];
    const isActivity = isActivityRow(grouped);
    const activityAbove =
      isActivity && !!prevGrouped && isActivityRow(prevGrouped);
    const activityBelow =
      isActivity && !!nextGrouped && isActivityRow(nextGrouped);

    const currentTimestamp = rowTimestamps[index]!;
    const prevTimestamp = rowTimestamps[index - 1] ?? null;
    const showDateMarker =
      currentTimestamp > 0 &&
      (prevTimestamp === null ||
        getDateKey(currentTimestamp) !== getDateKey(prevTimestamp));
    const rowDateMarker = showDateMarker ? (
      <DateMarker key={`date-${getDateKey(currentTimestamp)}`}>
        <DateBadge>
          <Text variant="text-small" color="var(--text-secondary)">
            {formatDateLabel(currentTimestamp)}
          </Text>
        </DateBadge>
      </DateMarker>
    ) : null;
    if (startsPrivate) privateDateMarkers.set(index, rowDateMarker);
    const dateMarker = startsPrivate ? null : rowDateMarker;

    if (grouped.type === "userActionGroup") {
      return (
        <UserActionGroupRow
          key={grouped.groupId}
          grouped={grouped}
          dateMarker={dateMarker}
          activityAbove={activityAbove}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    if (grouped.type === "toolGroup") {
      return (
        <ToolGroupRow
          key={`activity-${grouped.groupId}`}
          grouped={grouped}
          prevGrouped={prevGrouped}
          nextGrouped={nextGrouped}
          dateMarker={dateMarker}
          streamingEntries={streamingEntries}
        />
      );
    }

    const entry = grouped.entry;

    const kickoff = resolveKickoff(entry);
    if (kickoff) {
      return (
        <KickoffRow
          key={entry.id}
          kickoff={kickoff}
          dateMarker={dateMarker}
          activityAbove={activityAbove}
          activityBelow={activityBelow}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    const actionLine = resolveActionLine(entry);
    if (actionLine) {
      return (
        <ActivityLineRow
          key={entry.id}
          actionLine={actionLine}
          dateMarker={dateMarker}
          activityAbove={activityAbove}
          activityBelow={activityBelow}
          hasPrev={!!prevGrouped}
          hasNext={!!nextGrouped}
        />
      );
    }

    return (
      <MessageRow
        key={entry.id}
        entry={entry}
        nextGrouped={nextGrouped}
        interrupted={grouped.interrupted ?? false}
        dateMarker={dateMarker}
        activityAbove={activityAbove}
        activityBelow={activityBelow}
        streamingEntries={streamingEntries}
      />
    );
  });
  // What only this user sees reads as one block, separate from the shared conversation.
  const sections: React.ReactNode[] = [];
  for (let index = 0; index < rows.length;) {
    if (!privateRows[index]) {
      sections.push(rows[index]);
      index++;
      continue;
    }
    const start = index;
    while (privateRows[index]) index++;
    sections.push(
      <Fragment key={`private-${start}`}>
        <PrivateSection data-private="section">
          {privateDateMarkers.get(start)}
          {rows.slice(start, index)}
        </PrivateSection>
        <PrivateLabel data-private="label">Only you see this</PrivateLabel>
      </Fragment>
    );
  }
  const hasCitations = entries.some((entry) =>
    (
      streamingEntries.get(entry.id)?.text ??
      entry.assistantMessage?.text ??
      ""
    ).includes("(#source-")
  );
  return (
    <ResearchCitations
      entries={entries}
      streamingEntries={streamingEntries}
      enabled={hasCitations}
    >
      <div data-conversation-transcript style={{ display: "contents" }}>
        {sections}
        {reviewVersion && (
          <WorkflowChangeReview
            key={reviewVersion.commitId}
            version={reviewVersion}
            entries={entries}
            onShowDiscussion={showDiscussion}
          />
        )}
        {reviewVersion?.pushRequest && (
          <PushLiveRequestedNote
            approvedBy={reviewVersion.pushRequest.approvedBy}
          />
        )}
      </div>
    </ResearchCitations>
  );
});
